// Client-side Excel Template Parser Engine using SheetJS (xlsx) per ORIGINAL_REQUEST § R2 & PROJECT.md
import * as XLSX from 'xlsx';
import type {
  ChecklistItem,
  ChecklistTemplate,
  FrequencyType,
  LubricantSpec,
} from '../../types';
import {
  inferInputTypeFromText,
  extractGaugeBounds,
  extractSelectOptions,
  extractHoursThreshold,
} from './regexInference';

export interface ParsedSheetData {
  sheetName: string;
  frequency: FrequencyType | 'LUBRICATION' | 'UNKNOWN';
  items: ChecklistItem[];
  lubricants?: LubricantSpec[];
  safetyCautions?: string[];
}

export interface ParsedWorkbookResult {
  fileName: string;
  sheets: ParsedSheetData[];
  allItems: ChecklistItem[];
  lubricants: LubricantSpec[];
  safetyCautions: string[];
  summary: {
    totalSheets: number;
    totalItems: number;
    dailyCount: number;
    weeklyCount: number;
    monthlyCount: number;
    hoursCount: number;
  };
}

/**
 * Classify worksheet name into FrequencyType, LUBRICATION, or UNKNOWN
 */
export function classifySheet(sheetName: string): FrequencyType | 'LUBRICATION' | 'UNKNOWN' {
  const norm = sheetName.toLowerCase().trim();
  if (norm.includes('หล่อลื่น') || norm.includes('lube') || norm.includes('lubricat')) {
    return 'LUBRICATION';
  }
  if (norm.includes('daily') || norm.includes('shift') || norm.includes('ประจำวัน') || norm.includes('กะ')) {
    return 'DAILY';
  }
  if (norm.includes('weekly') || norm.includes('สัปดาห์')) {
    return 'WEEKLY';
  }
  if (norm.includes('monthly') || norm.includes('ประจำเดือน') || norm.includes('เดือน')) {
    return 'MONTHLY';
  }
  if (norm.includes('yearly') || norm.includes('hour') || norm.includes('ชั่วโมง') || norm.includes('รอบปี') || norm.includes('operating')) {
    return 'HOURS';
  }
  return 'UNKNOWN';
}

/**
 * Find index of header row dynamically by searching for anchor keywords
 */
export function findHeaderRowIndex(sheetRows: any[][]): number {
  for (let r = 0; r < Math.min(15, sheetRows.length); r++) {
    const row = sheetRows[r];
    if (!Array.isArray(row) || row.length === 0) continue;
    const lineStr = row.map((cell) => String(cell || '')).join(' ').toLowerCase();
    if (
      (lineStr.includes('ลำดับ') || lineStr.includes('no') || lineStr.includes('#')) &&
      (lineStr.includes('รายการ') || lineStr.includes('task') || lineStr.includes('inspection') || lineStr.includes('ตรวจ'))
    ) {
      return r;
    }
  }
  return -1;
}

/**
 * Parse standard 7-column rows into ChecklistItems
 */
export function parseChecklistSheetRows(
  rows: any[][],
  frequency: FrequencyType,
  sheetName: string = ''
): ChecklistItem[] {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error(`Sheet "${sheetName}" is completely empty`);
  }

  // Remove trailing completely empty rows
  const cleanRows = [...rows];
  while (cleanRows.length > 0 && (!cleanRows[cleanRows.length - 1] || cleanRows[cleanRows.length - 1].every((c: any) => !c && c !== 0))) {
    cleanRows.pop();
  }

  const headerRowIndex = findHeaderRowIndex(cleanRows);
  if (headerRowIndex === -1) {
    // If no explicit header keyword found, check if row 0 starts with numeric item
    if (cleanRows.length > 0 && !isNaN(Number(cleanRows[0][0])) && Number(cleanRows[0][0]) > 0) {
      // Header is at row 0 or missing, proceed with row 0
    } else {
      throw new Error(`Header row not found in sheet "${sheetName}": missing "No." or "ลำดับ" column`);
    }
  }

  const dataRows = headerRowIndex !== -1 ? cleanRows.slice(headerRowIndex + 1) : cleanRows;
  const items: ChecklistItem[] = [];

  let fallbackItemNo = 1;

  for (const row of dataRows) {
    if (!Array.isArray(row) || row.length === 0) continue;
    
    // Stop at signature or footer blocks
    const rowJoined = row.map((c) => String(c || '')).join(' ');
    if (rowJoined.includes('ลงชื่อ') || rowJoined.includes('Inspector') || rowJoined.includes('Supervisor') || rowJoined.includes('ข้อเสนอแนะ')) {
      break;
    }

    const itemNoCandidate = Number(row[0]);
    const itemNo = !isNaN(itemNoCandidate) && itemNoCandidate > 0 ? itemNoCandidate : fallbackItemNo;

    const taskText = String(row[1] || '').trim();
    const criteriaText = String(row[2] || '').trim();
    const methodText = String(row[3] || '').trim();
    const layoutText = String(row[4] || '').trim();
    const limitsText = String(row[5] || '').trim();

    // Skip empty task rows
    if (!taskText) continue;

    const inputType = inferInputTypeFromText(taskText, criteriaText, methodText, layoutText);
    const id = `${frequency.toLowerCase()}_${itemNo}`;

    const item: ChecklistItem = {
      id,
      itemNo,
      frequency,
      taskNameTh: taskText,
      criteriaTh: criteriaText || 'ผ่านเกณฑ์มาตรฐานความปลอดภัย',
      methodTh: methodText || 'สายตา (Visual)',
      inputType,
    };

    if (inputType === 'numeric_gauge') {
      const bounds = extractGaugeBounds(taskText, criteriaText, methodText, limitsText);
      item.unit = bounds.unit || 'MPa';
      if (bounds.minValue !== undefined) item.minValue = bounds.minValue;
      if (bounds.maxValue !== undefined) item.maxValue = bounds.maxValue;
      if (bounds.criticalLimit) item.criticalLimit = bounds.criticalLimit;
    } else if (inputType === 'select_option') {
      item.options = extractSelectOptions(layoutText, criteriaText);
    }

    if (frequency === 'HOURS') {
      const hours = extractHoursThreshold(taskText, criteriaText);
      item.hoursThreshold = hours || 500;
    }

    items.push(item);
    fallbackItemNo++;
  }

  return items;
}

/**
 * Parse Lubrication Guide sheet
 */
export function parseLubricationSheetRows(rows: any[][]): {
  lubricants: LubricantSpec[];
  safetyCautions: string[];
} {
  const lubricants: LubricantSpec[] = [];
  const safetyCautions: string[] = [];

  for (const row of rows) {
    if (!Array.isArray(row) || row.length === 0) continue;
    const textJoined = row.map((c) => String(c || '')).join(' ');

    // Check for safety cautions
    if (textJoined.includes('ข้อควรระวัง') || textJoined.includes('Caution') || textJoined.includes('ห้ามใช้ลมเป่า')) {
      safetyCautions.push(textJoined.trim());
      continue;
    }
    if (/^\d+\.\s+/.test(textJoined.trim()) && (textJoined.includes('ห้าม') || textJoined.includes('ไฮดรอลิก') || textJoined.includes('แกน W'))) {
      safetyCautions.push(textJoined.trim());
      continue;
    }

    // Check for lubricant rows
    const symbol = String(row[0] || '').trim();
    if (symbol && (symbol.includes('O -') || symbol.includes('Klüber') || symbol.includes('Castrol') || symbol.includes('Molykote') || symbol.includes('ISO VG'))) {
      lubricants.push({
        symbol,
        name: String(row[1] || symbol).trim(),
        specification: String(row[2] || '').trim(),
        equivalents: String(row[3] || '').split(/•|\n/).map((s) => s.trim()).filter(Boolean),
        points: String(row[4] || '').trim(),
        intervalAndQuantity: String(row[5] || '').trim(),
      });
    }
  }

  return { lubricants, safetyCautions };
}

/**
 * Parse an Excel file (File, ArrayBuffer, or Uint8Array) into structured checklist data
 */
export async function parseExcelWorkbook(
  data: File | ArrayBuffer | Uint8Array,
  fileName: string = 'imported_template.xlsx'
): Promise<ParsedWorkbookResult> {
  let arrayBuffer: ArrayBuffer;

  if (typeof File !== 'undefined' && data instanceof File) {
    fileName = data.name;
    arrayBuffer = await data.arrayBuffer();
  } else if (data instanceof Uint8Array) {
    arrayBuffer = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
  } else {
    arrayBuffer = data as ArrayBuffer;
  }

  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  if (!workbook || !workbook.SheetNames || workbook.SheetNames.length === 0) {
    throw new Error('Invalid Excel file: No sheets found');
  }

  const sheets: ParsedSheetData[] = [];
  const allItems: ChecklistItem[] = [];
  let lubricants: LubricantSpec[] = [];
  let safetyCautions: string[] = [];

  for (const sheetName of workbook.SheetNames) {
    const worksheet = workbook.Sheets[sheetName];
    if (!worksheet) continue;

    // Convert sheet to 2D array of cell values
    const rows = XLSX.utils.sheet_to_json<any[]>(worksheet, {
      header: 1,
      defval: '',
      raw: false,
    });

    if (!rows || rows.length === 0) continue;

    const classification = classifySheet(sheetName);

    if (classification === 'LUBRICATION') {
      const lubeData = parseLubricationSheetRows(rows);
      lubricants = [...lubricants, ...lubeData.lubricants];
      safetyCautions = [...safetyCautions, ...lubeData.safetyCautions];
      sheets.push({
        sheetName,
        frequency: 'LUBRICATION',
        items: [],
        lubricants: lubeData.lubricants,
        safetyCautions: lubeData.safetyCautions,
      });
    } else {
      const freq: FrequencyType =
        classification === 'UNKNOWN' ? 'DAILY' : classification;
      try {
        const sheetItems = parseChecklistSheetRows(rows, freq, sheetName);
        if (sheetItems.length > 0) {
          items_append: {
            // Deduplicate item IDs
            for (const item of sheetItems) {
              const existingWithId = allItems.find((i) => i.id === item.id);
              if (existingWithId) {
                item.id = `${item.id}_${sheetItems.indexOf(item) + 1}`;
              }
              allItems.push(item);
            }
          }
          sheets.push({
            sheetName,
            frequency: freq,
            items: sheetItems,
          });
        }
      } catch (err) {
        console.warn(`[ExcelParser] Skipping unparsable sheet "${sheetName}":`, err);
      }
    }
  }

  const summary = {
    totalSheets: sheets.length,
    totalItems: allItems.length,
    dailyCount: allItems.filter((i) => i.frequency === 'DAILY').length,
    weeklyCount: allItems.filter((i) => i.frequency === 'WEEKLY').length,
    monthlyCount: allItems.filter((i) => i.frequency === 'MONTHLY').length,
    hoursCount: allItems.filter((i) => i.frequency === 'HOURS').length,
  };

  return {
    fileName,
    sheets,
    allItems,
    lubricants,
    safetyCautions,
    summary,
  };
}
